<script setup>
import { translate } from '@/plugins/i18n.js'
import { WALKER_COMMANDS } from '@/services/helpers/ElbwalkerHelper'
import { elb } from '@elbwalker/walker.js'
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps({
  emailRegex: RegExp
})

const loading = ref(false)
const done = ref(false)
const email = ref('')
const form = ref(null)
const emits = defineEmits(['showLogin'])

const router = useRouter()

const rules = {
  required: (value) => !!value || translate('validation_msg_required'),
  email: (value) =>
    props.emailRegex.test(value) || translate('validation_msg_email')
}

const mailInfo = computed(() =>
  translate('reset_mail_sent', `<b>${email.value}</b>`)
)

const el = ref(null)

onMounted(() => {
  elb(WALKER_COMMANDS.init, el.value)
})

async function changeAction() {
  if (router.currentRoute?.value?.path === '/forgot-password') {
    router.push('/auth/v2/login')
  }
}

async function reset() {
  const [{ default: ApiController }, { default: MessageHelper }] =
    await Promise.all([
      import('@/services/ApiController'),
      import('@/services/helpers/MessageHelper')
    ])

  if (!formIsValid) {
    await MessageHelper.addErrorMessage('please_check_your_data')
    return
  }

  loading.value = true
  try {
    await ApiController.resetPassword(email.value)
    done.value = true
  } catch (e) {
    const { status, error } = e
    switch (status) {
      case 400:
        await MessageHelper.addErrorMessage(
          'please_check_your_data',
          null,
          5000,
          e
        )
        break
      case 409:
        await MessageHelper.addErrorMessage(
          'login_error_invalid_email',
          null,
          5000,
          e
        )
        break
      default:
        await MessageHelper.addErrorMessage('unknown_error', null, 5000, e)
    }
  }
  loading.value = false
}

const formIsValid = computed(() => {
  return !!form.value?.isValid
})
</script>

<template>
  <div
    ref="el"
    class="d-flex flex-column"
    data-elb="account"
    data-elb-account="action:forgot_password"
    data-elbaction="visible"
  >
    <div v-if="!done">
      <span class="text-center">{{ translate('reset_password_subline') }}</span>
      <VForm
        ref="form"
        lazy-validation
        class="d-flex flex-column justify-center mt-5"
        @submit.prevent="reset"
      >
        <div>
          <VTextField
            type="email"
            v-model="email"
            :rules="[rules.required, rules.email]"
            :label="translate('fe_app_label_email')"
            @keydown.enter="changeAction"
          />
        </div>
        <div>
          <VBtn
            color="primary"
            class="mt-3"
            @click="reset"
            :loading="loading"
            :disabled="loading || !formIsValid"
            depressed
            block
          >
            {{ translate('reset_password') }}
          </VBtn>
        </div>
      </VForm>
    </div>
    <div class="d-flex flex-column justify-start justify-lg-center" v-else>
      <div class="text-center" v-html="mailInfo"></div>
    </div>
    <VBtnSecondary
      @click="emits('showLogin')"
      class="mt-3"
      :disabled="loading"
      block
    >
      {{ translate('login') }}
    </VBtnSecondary>
  </div>
</template>

<style scoped>
.actions > * {
  min-width: 100%;
}
</style>
